/**
 * Enrichment Service
 * Handles AI-powered clock enrichment functionality
 * VERSION: 2025-01-XX-ENRICHMENT-MODAL
 */
console.log('🚀 enrichment-service.js LOADED');
console.log('🔍 Script loading timestamp:', new Date().toISOString());

class EnrichmentService {
    constructor() {
        console.log('🔧 EnrichmentService constructor called');
        this.modal = null;
        this.container = null;
        this.closeBtn = null;
        this.isProcessing = false;
        this.currentClockId = null;
        
        this.init();
    }

    /**
     * Initialize the enrichment service
     */
    init() {
        console.log('🔧 EnrichmentService.init() called');
        console.log('🔍 Document ready state:', document.readyState);
        console.log('🔍 Looking for modal elements...');
        
        this.modal = document.getElementById('enrichmentModal');
        this.container = document.getElementById('enrichmentContainer');
        this.closeBtn = document.getElementById('closeEnrichment');
        
        console.log('🔍 Modal element found:', !!this.modal);
        console.log('🔍 Container element found:', !!this.container);
        console.log('🔍 Close button found:', !!this.closeBtn);
        
        if (!this.modal || !this.container || !this.closeBtn) {
            console.error('❌ Enrichment modal elements not found');
            console.log('🔍 Available elements with "enrichment" in ID:', 
                Array.from(document.querySelectorAll('[id*="enrichment"]')).map(el => el.id));
            console.log('🔍 Available elements with "modal" in ID:', 
                Array.from(document.querySelectorAll('[id*="modal"]')).map(el => el.id));
            return;
        }

        this.setupEventListeners();
        console.log('✅ Enrichment service initialized successfully');
    }

    /**
     * Setup event listeners for the modal
     */
    setupEventListeners() {
        // Close button
        this.closeBtn.addEventListener('click', () => {
            this.closeModal();
        });

        // Close on backdrop click
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.closeModal();
            }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.classList.contains('active')) {
                this.closeModal();
            }
        });

        document.addEventListener('click', async (e) => {
            const target = e.target;
            
            // Handle edit button clicks
            if (target && (target.id === 'edit-building-name' || target.classList.contains('edit-btn'))) {
                const fieldPath = target.dataset.fieldpath || 'building_info.name';
                const targetId = target.dataset.target || 'enrich-building-name';
                this.startInlineEdit(targetId, fieldPath);
                return;
            }
            
            // Handle save button clicks
            if (target && target.classList.contains('save-edit-btn')) {
                const targetId = target.dataset.target;
                const fieldPath = target.dataset.fieldpath;
                await this.saveInlineEdit(targetId, fieldPath);
                return;
            }
            
            // Handle cancel button clicks
            if (target && target.classList.contains('cancel-edit-btn')) {
                const targetId = target.dataset.target;
                this.cancelInlineEdit(targetId);
                return;
            }
        });
    }

    /**
     * Format denomination for display
     */
    formatDenomination(denomination) {
        if (!denomination) return 'Unknown';
        const map = {
            'anglican': 'Anglican (Church of England)',
            'catholic': 'Catholic',
            'roman_catholic': 'Roman Catholic',
            'methodist': 'Methodist',
            'baptist': 'Baptist',
            'presbyterian': 'Presbyterian',
            'united_reformed': 'United Reformed',
            'congregational': 'Congregational',
            'quaker': 'Quaker',
            'evangelical': 'Evangelical',
            'pentecostal': 'Pentecostal',
            'orthodox': 'Orthodox',
            'lutheran': 'Lutheran'
        };
        return map[denomination.toLowerCase()] || denomination.charAt(0).toUpperCase() + denomination.slice(1).replace(/_/g, ' ');
    }

    /**
     * Format religion for display
     */
    formatReligion(religion) {
        if (!religion) return 'Unknown';
        const map = {
            'christian': 'Christian',
            'jewish': 'Jewish',
            'muslim': 'Muslim',
            'hindu': 'Hindu',
            'buddhist': 'Buddhist',
            'sikh': 'Sikh',
            'multifaith': 'Multifaith'
        };
        return map[religion.toLowerCase()] || religion.charAt(0).toUpperCase() + religion.slice(1);
    }

    /**
     * Format building type for display
     */
    formatBuildingType(buildingType) {
        if (!buildingType) return 'Unknown';
        const map = {
            'church': 'Church',
            'chapel': 'Chapel',
            'cathedral': 'Cathedral',
            'abbey': 'Abbey',
            'priory': 'Priory',
            'minster': 'Minster',
            'basilica': 'Basilica',
            'meeting_house': 'Meeting House'
        };
        return map[buildingType.toLowerCase()] || buildingType.charAt(0).toUpperCase() + buildingType.slice(1).replace(/_/g, ' ');
    }

    /**
     * Generate a coherent summary from enrichment data
     * Builds complete sentences instead of stitching fragments
     */
    generateSummary(data) {
        const sentences = [];

        // Building and heritage sentence
        const buildingName = data.building_info?.name;
        const heritageStatus = data.heritage?.status;
        const heritageGrade = data.heritage?.grade;

        if (buildingName) {
            if (heritageStatus && heritageStatus !== 'Not listed' && heritageStatus !== 'Unknown') {
                const gradeText = heritageGrade ? ` (${heritageGrade})` : '';
                sentences.push(`${buildingName} is a ${heritageStatus}${gradeText}.`);
            } else {
                sentences.push(`${buildingName}.`);
            }
        }

        // Clock mounting - use the AI description if it's a proper sentence
        const mount = data.clock_features?.mount;
        if (mount && mount.length > 10) {
            // If it starts with lowercase or doesn't end with period, make it a sentence
            let mountSentence = mount.trim();
            if (!/^[A-Z]/.test(mountSentence)) {
                mountSentence = 'The clock is mounted ' + mountSentence;
            }
            if (!/[.!?]$/.test(mountSentence)) {
                mountSentence += '.';
            }
            // Avoid double periods
            mountSentence = mountSentence.replace(/\.+$/, '.');
            sentences.push(mountSentence);
        }

        // Clock dial description
        const dial = data.clock_features?.dial;
        if (dial && dial.length > 10) {
            let dialSentence = dial.trim();
            if (!/^[A-Z]/.test(dialSentence)) {
                dialSentence = 'The dial features ' + dialSentence;
            }
            if (!/[.!?]$/.test(dialSentence)) {
                dialSentence += '.';
            }
            dialSentence = dialSentence.replace(/\.+$/, '.');
            sentences.push(dialSentence);
        }

        // Numerals - only if concise
        const numerals = data.clock_features?.numerals;
        if (numerals && numerals.length > 5 && numerals.length < 100) {
            let numeralSentence = numerals.trim();
            if (!/^[A-Z]/.test(numeralSentence) || !numeralSentence.includes(' ')) {
                // Simple value like "Roman" or "Arabic"
                numeralSentence = `The clock displays ${numeralSentence.toLowerCase()} numerals.`;
            } else if (!/[.!?]$/.test(numeralSentence)) {
                numeralSentence += '.';
            }
            numeralSentence = numeralSentence.replace(/\.+$/, '.');
            sentences.push(numeralSentence);
        }

        // Faces count if multiple
        const facesCount = data.clock_features?.faces_observed;
        if (facesCount && facesCount > 1) {
            sentences.push(`The clock has ${facesCount} visible faces.`);
        }

        // Clockmaker
        const maker = data.clock_features?.maker;
        if (maker && maker !== 'Unknown' && maker.length > 2) {
            sentences.push(`Made by ${maker}.`);
        }

        // Movement date (distinct from building date)
        const movementDate = data.clock_features?.movement_date;
        if (movementDate && movementDate !== 'Unknown' && movementDate.length > 2) {
            sentences.push(`The clock mechanism dates from ${movementDate}.`);
        }

        // Frame type
        const frameType = data.clock_features?.frame_type;
        if (frameType && frameType !== 'Unknown' && frameType.length > 2) {
            sentences.push(`It has a ${frameType.toLowerCase()} frame.`);
        }

        // Number of trains
        const trains = data.clock_features?.number_of_trains;
        if (trains && trains > 0) {
            const trainDesc = trains === 1 ? 'a single train (time only)' :
                              trains === 2 ? 'two trains (going and striking)' :
                              trains === 3 ? 'three trains (going, striking and chiming)' :
                              `${trains} trains`;
            sentences.push(`The movement has ${trainDesc}.`);
        }

        // Fallback if no sentences generated
        if (sentences.length === 0) {
            return 'Enrichment data available - click individual fields above for details.';
        }

        return sentences.join(' ');
    }

    /**
     * Start inline editing for a field
     */
    startInlineEdit(targetId, fieldPath) {
        const span = document.getElementById(targetId);
        if (!span) return;
        
        const currentValue = span.textContent;
        const fieldName = fieldPath.split('.').pop();
        
        // Create textarea for multi-line editing
        const textarea = document.createElement('textarea');
        textarea.value = currentValue;
        textarea.className = 'inline-edit-textarea';
        textarea.style.cssText = `
            width: 100%;
            min-height: 60px;
            max-height: 200px;
            padding: 8px;
            border: 2px solid #2c5aa0;
            border-radius: 4px;
            font-size: 0.875rem;
            font-family: inherit;
            line-height: 1.4;
            resize: vertical;
            margin-bottom: 8px;
            box-sizing: border-box;
        `;
        
        // Create button container
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 8px;
            margin-top: 8px;
        `;
        
        // Create save and cancel buttons
        const saveBtn = document.createElement('button');
        saveBtn.className = 'save-edit-btn';
        saveBtn.textContent = 'Save';
        saveBtn.dataset.target = targetId;
        saveBtn.dataset.fieldpath = fieldPath;
        saveBtn.style.cssText = `
            background: #28a745;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 0.75rem;
            cursor: pointer;
            font-weight: 500;
        `;
        
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'cancel-edit-btn';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.dataset.target = targetId;
        cancelBtn.style.cssText = `
            background: #6c757d;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            font-size: 0.75rem;
            cursor: pointer;
            font-weight: 500;
        `;
        
        buttonContainer.appendChild(saveBtn);
        buttonContainer.appendChild(cancelBtn);
        
        // Store original content for cancel
        span.dataset.originalValue = currentValue;
        span.dataset.fieldPath = fieldPath;
        
        // Replace span content with textarea and buttons
        span.innerHTML = '';
        span.appendChild(textarea);
        span.appendChild(buttonContainer);
        
        // Focus and select textarea text
        textarea.focus();
        textarea.select();
        
        // Auto-resize textarea based on content
        const autoResize = () => {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
        };
        autoResize();
        textarea.addEventListener('input', autoResize);
        
        // Handle keyboard shortcuts
        textarea.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.ctrlKey) {
                // Ctrl+Enter to save
                e.preventDefault();
                this.saveInlineEdit(targetId, fieldPath);
            } else if (e.key === 'Escape') {
                this.cancelInlineEdit(targetId);
            }
        });
    }

    /**
     * Save inline edit
     */
    async saveInlineEdit(targetId, fieldPath) {
        const span = document.getElementById(targetId);
        if (!span) return;
        
        const textarea = span.querySelector('.inline-edit-textarea');
        if (!textarea) return;
        
        const newValue = textarea.value.trim();
        if (!newValue) {
            alert('Value cannot be empty');
            return;
        }
        
        try {
            const token = localStorage.getItem('authToken');
            if (!token) {
                alert('You must be logged in to edit');
                return;
            }
            
            const payload = {
                clockId: this.currentClockId,
                fieldPath: fieldPath,
                newValue: newValue,
                editReason: 'Quick correction from UI'
            };
            
            const ENRICHMENT_API_URL = 'https://b1oha2wc98.execute-api.us-east-1.amazonaws.com/prod/enrichment';
            const res = await fetch(ENRICHMENT_API_URL, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || `Edit failed: ${res.status}`);
            }
            
            // Update the span with new value
            span.textContent = newValue;
            span.removeAttribute('data-original-value');
            span.removeAttribute('data-field-path');
            
        } catch (err) {
            console.error('Edit failed', err);
            alert(`Edit failed: ${err.message}`);
        }
    }

    /**
     * Cancel inline edit
     */
    cancelInlineEdit(targetId) {
        const span = document.getElementById(targetId);
        if (!span) return;
        
        const originalValue = span.dataset.originalValue;
        if (originalValue !== undefined) {
            span.textContent = originalValue;
            span.removeAttribute('data-original-value');
            span.removeAttribute('data-field-path');
        }
    }

    /**
     * Open enrichment modal
     */
    async openEnrichmentModal(clockId, clockData) {
        console.log('🔧 openEnrichmentModal called with:', { clockId, clockData });
        
        if (!this.modal || !this.container) {
            console.error('❌ Modal elements not found');
            return;
        }
        
        this.currentClockId = clockId;
        this.currentClockData = clockData || {};
        this.isProcessing = true;
        
        // Show loading state
        this.container.innerHTML = `
            <div class="loading-state">
                <div class="spinner"></div>
                <p>🔍 Checking for existing data...</p>
                <p class="loading-details">Retrieving enrichment information</p>
            </div>
        `;
        
        this.modal.classList.add('active');
        
        try {
            console.log('🚀 Checking enrichment data for clock:', clockId);
            
            const enrichmentData = await this.callEnrichmentAPI(clockId, clockData);
            this.renderEnrichmentData(enrichmentData);
            
        } catch (error) {
            console.error('❌ Enrichment data retrieval failed:', error);
            
            this.container.innerHTML = `
                <div class="error-state">
                    <h3>❌ Data Retrieval Failed</h3>
                    <p>${error.message}</p>
                    <button onclick="window.enrichmentService.openEnrichmentModal('${clockId}', ${JSON.stringify(clockData)})" class="retry-btn">
                        🔄 Try Again
                    </button>
                </div>
            `;
        } finally {
            this.isProcessing = false;
        }
    }

    /**
     * Call enrichment API
     */
    async callEnrichmentAPI(clockId, clockData) {
        try {
            console.log('📡 Making enrichment API call for clock:', clockId);
            console.log('🔍 Clock data being sent:', clockData);
            
            const token = localStorage.getItem('authToken');
            console.log('🔑 Token available:', !!token);
            
            // Fallback: if no coordinates present in clock data, try DOM data attributes
            let lat = clockData.coordinates?.latitude || clockData.LATITUDE || clockData.latitude || null;
            let lon = clockData.coordinates?.longitude || clockData.LONGITUDE || clockData.longitude || null;
            if ((lat === null || lat === undefined) || (lon === null || lon === undefined)) {
                const latAttr = document.querySelector('[data-latitude]')?.dataset?.latitude;
                const lonAttr = document.querySelector('[data-longitude]')?.dataset?.longitude;
                const latNum = latAttr !== undefined ? parseFloat(latAttr) : NaN;
                const lonNum = lonAttr !== undefined ? parseFloat(lonAttr) : NaN;
                if (Number.isFinite(latNum) && Number.isFinite(lonNum)) {
                    lat = latNum;
                    lon = lonNum;
                    console.log('📍 Using DOM data-* attribute coordinates fallback:', { latitude: lat, longitude: lon });
                } else {
                    console.log('⚠️ No coordinates available in clock data or DOM attributes');
                }
            }

            const payload = {
                clockId: clockId,
                clockData: {
                    title: clockData.TITLE || clockData.title,
                    description: clockData.DESCRIPTION || clockData.description,
                    coordinates: { latitude: lat, longitude: lon }
                }
            };
            
            console.log('🔍 Raw clock data location:', clockData.location);
            console.log('🔍 Raw clock data LATITUDE:', clockData.LATITUDE);
            console.log('🔍 Raw clock data LONGITUDE:', clockData.LONGITUDE);
            console.log('🔍 Raw clock data latitude:', clockData.latitude);
            console.log('🔍 Raw clock data longitude:', clockData.longitude);
            console.log('🔍 Raw clock data exifData:', clockData.exifData);
            console.log('🔍 Raw clock data coordinates:', clockData.coordinates);
            console.log('🔍 Raw clock data locationSource:', clockData.locationSource);
            console.log('🔍 Parsed coordinates:', payload.clockData.coordinates);
            console.log('📤 API payload:', payload);
            const ENRICHMENT_API_URL = 'https://b1oha2wc98.execute-api.us-east-1.amazonaws.com/prod/enrichment';
            console.log('🌐 API URL:', ENRICHMENT_API_URL);
            
            // Make API call to enrichment endpoint
            const headers = {
                'Content-Type': 'application/json'
            };

            // Only include Authorization header if token exists
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(ENRICHMENT_API_URL, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(payload)
            });
            
            console.log('📥 API response status:', response.status);
            console.log('📥 API response headers:', Object.fromEntries(response.headers.entries()));
            
            if (!response.ok) {
                const errorData = await response.json();
                console.error('❌ API error response:', errorData);
                throw new Error(errorData.message || `API call failed with status ${response.status}`);
            }
            
            const result = await response.json();
            console.log('📥 API response data:', result);
            console.log('📥 API response data.data:', result.data);
            console.log('📥 API response data.data.sources:', result.data?.sources);
            console.log('📥 API response data.data.data_quality:', result.data?.data_quality);
            console.log('📥 API response data.data.data_quality.data_sources:', result.data?.data_quality?.data_sources);
            console.log('📥 API response data.data.building_info:', result.data?.building_info);
            console.log('📥 API response data.data.location:', result.data?.location);
            console.log('📥 API response data.data.heritage:', result.data?.heritage);
            
            if (result.success) {
                console.log('✅ Enrichment API call successful');
                
                // Cache the result
                this.cacheEnrichment(clockId, result.data);
                
                return result.data;
            } else {
                throw new Error(result.message || 'Enrichment failed');
            }
            
        } catch (error) {
            console.error('❌ Enrichment API call failed:', error);
            
            // Don't cache failed requests - let them retry
            console.log('🔄 API call failed, not caching mock data to allow retry');
            throw error; // Re-throw the error so the UI can handle it
        }
    }

    /**
     * Get cached enrichment data
     */
    getCachedEnrichment(clockId) {
        const cacheKey = `enrichment_${clockId}`;
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
            try {
                const data = JSON.parse(cached);
                const now = Date.now();
                
                // Check if cache is still valid (24 hours)
                if (data.timestamp && (now - data.timestamp) < (24 * 60 * 60 * 1000)) {
                    console.log('✅ Using cached enrichment data (age:', Math.round((now - data.timestamp) / 1000 / 60), 'minutes)');
                    return data.enrichment;
                } else {
                    console.log('⚠️ Cached enrichment data expired, removing');
                    localStorage.removeItem(cacheKey);
                }
            } catch (error) {
                console.error('❌ Error parsing cached enrichment data:', error);
                localStorage.removeItem(cacheKey);
            }
        }
        
        return null;
    }

    /**
     * Cache enrichment data
     */
    cacheEnrichment(clockId, enrichmentData) {
        const cacheKey = `enrichment_${clockId}`;
        const cacheData = {
            enrichment: enrichmentData,
            timestamp: Date.now()
        };
        
        try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log('✅ Cached enrichment data for clock:', clockId);
        } catch (error) {
            console.error('❌ Error caching enrichment data:', error);
        }
    }

    /**
     * Get mock enrichment data for development
     */
    getMockEnrichmentData(clockData) {
        return {
            clock_features: {
                mount: "middle stage of the clock tower",
                dial: "decorative stone roundel, painted blue",
                numerals: "gold Roman Numerals",
                hands: "gold hands",
                faces_observed: 2,
                faces_description: "faces on the north, west, and south sides"
            },
            building_info: {
                name: clockData?.TITLE?.includes('St John') ? "St John the Evangelist Church" : "Historic Building",
                built: "begun in 1631, altered 1830-1838, restored 1866-1868",
                custodian: "Church Conservation Trust"
            },
            location: {
                address: clockData?.TITLE?.includes('Leeds') ? "Saint John the Evangelist's Church, New Briggate, Leeds" : "Historic location",
                admin_areas: ["Leeds", "England", "United Kingdom"]
            },
            heritage: {
                status: "redundant",
                grade: "likely listed but grade unspecified"
            },
            external_ids: {
                wikidata: "Q30161037",
                osm: "way/78507129"
            },
            sources: [
                { name: "Wikidata", url: "https://www.wikidata.org/wiki/Q30161037" },
                { name: "OpenStreetMap", url: "https://www.openstreetmap.org/way/78507129" }
            ],
            data_quality: {
                needs_review: true,
                review_notes: "Verify heritage listing grade and exact address details.",
                data_sources: {
                    ai_analysis: true,
                    wikidata: true,
                    osm: true,
                    geocoding: true,
                    cct: true
                }
            }
        };
    }

    /**
     * Show loading state in modal
     */
    showLoadingState(message = 'Analyzing clock and gathering information...', showSpinner = true) {
        const spinnerHtml = showSpinner ? '<div class="loading-spinner"></div>' : '';
        const subtitleHtml = showSpinner ? '<p class="loading-subtitle">This may take up to 60 seconds</p>' : '';
        
        this.container.innerHTML = `
            <div class="enrichment-placeholder">
                <div class="enrichment-loading">
                    ${spinnerHtml}
                    <p>${message}</p>
                    ${subtitleHtml}
                </div>
            </div>
        `;
    }

    /**
     * Show error state in modal
     */
    showErrorState(errorMessage) {
        this.container.innerHTML = `
            <div class="enrichment-error">
                <h4>❌ Enrichment Failed</h4>
                <p>${errorMessage || 'Unable to enrich this clock at the moment.'}</p>
                <button class="retry-button" onclick="enrichmentService.retryEnrichment()">
                    Try Again
                </button>
            </div>
        `;
    }

    /**
     * Retry enrichment (forces fresh API call)
     */
    async retryEnrichment() {
        if (this.currentClockId) {
            // Clear cache for this clock to force fresh API call
            this.clearEnrichmentCache(this.currentClockId);
            
            // Get clock data from the page
            const clockData = this.getClockDataFromPage();
            await this.openEnrichmentModal(this.currentClockId, clockData);
        }
    }

    /**
     * Clear enrichment cache for a specific clock
     */
    clearEnrichmentCache(clockId) {
        const cacheKey = `enrichment_${clockId}`;
        localStorage.removeItem(cacheKey);
        console.log('🗑️ Cleared enrichment cache for clock:', clockId);
    }

    /**
     * Clear all enrichment cache
     */
    clearAllEnrichmentCache() {
        const keys = Object.keys(localStorage);
        const enrichmentKeys = keys.filter(key => key.startsWith('enrichment_'));
        
        enrichmentKeys.forEach(key => {
            localStorage.removeItem(key);
        });
        
        console.log('🗑️ Cleared all enrichment cache (', enrichmentKeys.length, 'entries)');
    }

    /**
     * Get clock data from the current page
     */
    getClockDataFromPage() {
        // Try to get clock data from the page context
        if (window.clockViewPage && window.clockViewPage.clock) {
            return window.clockViewPage.clock;
        }
        
        // Fallback: extract from page elements
        const titleElement = document.querySelector('.clock-title, h1');
        const descriptionElement = document.querySelector('.clock-description, .description');
        
        return {
            TITLE: titleElement?.textContent || 'Unknown Clock',
            DESCRIPTION: descriptionElement?.textContent || '',
            LATITUDE: document.querySelector('[data-latitude]')?.dataset.latitude || '',
            LONGITUDE: document.querySelector('[data-longitude]')?.dataset.longitude || ''
        };
    }

    /**
     * Render enrichment data in the modal
     */
    async renderEnrichmentData(data) {
        console.log('🔍 Rendering enrichment data:', data);
        console.log('📋 Sources received:', data.sources);
        let isAdmin = false;
        try {
            isAdmin = (window.authManager?.authState?.userRole === 'admin');
            if (!isAdmin) {
                const uiRaw = localStorage.getItem('userInfo');
                if (uiRaw && uiRaw !== 'undefined' && uiRaw !== 'null') {
                    const ui = JSON.parse(uiRaw);
                    isAdmin = (ui?.role === 'admin');
                }
            }
            if (!isAdmin) {
                const tok = localStorage.getItem('authToken');
                if (tok && tok !== 'undefined' && tok !== 'null' && tok.includes('.')) {
                    const payloadB64 = tok.split('.')[1].replace(/-/g,'+').replace(/_/g,'/');
                    try {
                        const payloadJson = JSON.parse(atob(payloadB64));
                        if (payloadJson?.role === 'admin') {
                            isAdmin = true;
                        }
                    } catch (_) {}
                }
            }
            console.log('🔐 Admin check →', { isAdmin });
        } catch (e) { isAdmin = false; }
        
        // Remove data status banner per UX
        const dataStatus = '';

        // Styled source badges
        const sourceClassMap = {
            'AI Analysis': 'ai-analysis',
            'Wikidata': 'wikidata',
            'OpenStreetMap': 'osm',
            'Google Maps': 'geocoding',
            'Church Conservation Trust': 'cct',
            'Historic England': 'historic-england',
            'MapIt': 'mapit',
            
            'Church Heritage Record': 'church-heritage',
            'AHS Turret Clock Data': 'ahs-data'
        };
        const actualSources = data.sources || [];

        // Check if AHS data is present and add to sources if not already there
        if (data.ahs_data?.ahs_clock_id || data.ahs_data?.construction_date || data.ahs_data?.ahs_notes) {
            const hasAhsSource = actualSources.some(s => s.name === 'AHS Turret Clock Data');
            if (!hasAhsSource) {
                actualSources.push({
                    name: 'AHS Turret Clock Data',
                    url: 'https://www.ahsoc.org/groups/turret-clock-group/'
                });
            }
        }

        const sourceBadges = actualSources.map(source => {
            const cls = sourceClassMap[source.name] || '';
            return `<span class="source-badge ${cls}">${source.name}</span>`;
        }).join('');

        // Create comprehensive system explanation (collapsible)
        const systemExplanation = `
            <div class="system-explanation">
                <div class="system-explanation-header" onclick="toggleSystemExplanation()">
                    <h4>How This System Works</h4>
                    <span class="system-explanation-toggle">▼</span>
                </div>
                <div class="system-explanation-content" style="display: none;">
                    <p>This enrichment system combines multiple authoritative sources to provide comprehensive information about each clock:</p>
                    <ul>
                        <li><strong>Wikidata:</strong> Searches the world's largest knowledge database for building information</li>
                        <li><strong>OpenStreetMap:</strong> Provides building identification and spatial data</li>
                        <li><strong>Google Maps:</strong> Converts coordinates to readable addresses and administrative areas</li>
                        <li><strong>MapIt:</strong> Civic context (constituency and local council) from MySociety's MapIt</li>
                        <li><strong>Church Conservation Trust:</strong> Identifies churches under CCT custodianship</li>
                        <li><strong>Historic England:</strong> Searches the National Heritage List for England for listed buildings and monuments</li>
                        
                        <li><strong>Church Heritage Record:</strong> Provides detailed information about church buildings and their heritage status</li>
                        <li><strong>AI Analysis:</strong> we pass the image to an LLM which extracts clock face information, and also summarises the information we get from verified data sources such as maps and wikidata.</li>
                    </ul>
                </div>
            </div>
        `;

        // Generate a proper summary from enrichment data
        const summaryText = this.generateSummary(data);

        const adminControls = isAdmin ? `
            <div class="admin-controls" style="margin:8px 0 12px 0">
                <button id="admin-refresh-all" class="edit-btn" style="margin-left:0">Re-run full enrichment</button>
                <span id="admin-refresh-all-status" class="meta" style="margin-left:8px"></span>
            </div>
        ` : '';

        this.container.innerHTML = `
            <div class="enrichment-data">
                <!-- Data Status Indicator -->
                ${dataStatus}
                
                <!-- System Explanation -->
                ${systemExplanation}
                ${adminControls}
                
                <!-- Data Sources -->
                <div class="data-sources-section">
                    <h4>📋 Available Data Sources</h4>
                    <div class="all-source-badges">
                        <span class="source-badge ai-analysis" title="AI Analysis: GPT-4o Mini generates detailed insights">🤖 AI Analysis</span>
                        <span class="source-badge wikidata" title="Wikidata: World's largest knowledge database">📚 Wikidata</span>
                        <span class="source-badge osm" title="OpenStreetMap: Building and spatial data">🗺️ OpenStreetMap</span>
                        <span class="source-badge geocoding" title="Google Maps: Address resolution from coordinates">📍 Google Maps</span>
                        <span class="source-badge mapit" title="MapIt: Civic context from constituency and councils">🏛️ MapIt</span>
                        <span class="source-badge cct" title="Church Conservation Trust: Church custodian detection">⛪ CCT Detection</span>
                        <span class="source-badge historic-england" title="Historic England: National Heritage List for England">🏛️ Historic England</span>
                        
                        <span class="source-badge church-heritage" title="Church Heritage Record: Church building information">⛪ Church Heritage</span>
                        <span class="source-badge ahs-data" title="AHS Turret Clock Data: Antiquarian Horological Society turret clock database">🕰️ AHS Data</span>
                    </div>
                    
                    <div class="active-sources">
                        <h5>✅ Sources Used for This Clock</h5>
                        <div class="active-source-badges">
                            ${sourceBadges || '<span class="no-sources">No additional sources available</span>'}
                        </div>
                    </div>
                </div>

                <!-- Clock Features -->
                <div class="enrichment-section">
                    <h4>🕐 Clock Details</h4>
                    <div class="clock-features">
                        <div class="feature-item">
                            <span class="feature-label">Mount:</span>
                            <span class="feature-value" id="enrich-feature-mount">${data.clock_features?.mount || 'Building facade'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.mount" data-target="enrich-feature-mount" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Dial:</span>
                            <span class="feature-value" id="enrich-feature-dial">${data.clock_features?.dial || 'Circular dial'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.dial" data-target="enrich-feature-dial" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Numerals:</span>
                            <span class="feature-value" id="enrich-feature-numerals">${data.clock_features?.numerals || 'Roman numerals'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.numerals" data-target="enrich-feature-numerals" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Hands:</span>
                            <span class="feature-value" id="enrich-feature-hands">${data.clock_features?.hands || 'Metal hands'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.hands" data-target="enrich-feature-hands" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Faces:</span>
                            <span class="feature-value" id="enrich-feature-faces">${(data.clock_features?.faces_observed || 1) === 1 ? 'Single face' : `${data.clock_features?.faces_observed} faces`}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.faces_description" data-target="enrich-feature-faces" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Maker:</span>
                            <span class="feature-value" id="enrich-feature-maker">${data.clock_features?.maker || 'Unknown'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.maker" data-target="enrich-feature-maker" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Movement Date:</span>
                            <span class="feature-value" id="enrich-feature-movement-date">${data.clock_features?.movement_date || 'Unknown'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.movement_date" data-target="enrich-feature-movement-date" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Trains:</span>
                            <span class="feature-value" id="enrich-feature-trains">${data.clock_features?.number_of_trains != null ? data.clock_features.number_of_trains : 'Unknown'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.number_of_trains" data-target="enrich-feature-trains" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Frame Type:</span>
                            <span class="feature-value" id="enrich-feature-frame-type">${data.clock_features?.frame_type || 'Unknown'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.frame_type" data-target="enrich-feature-frame-type" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Length:</span>
                            <span class="feature-value" id="enrich-feature-dim-l">${data.clock_features?.dimensions_l || 'Not recorded'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.dimensions_l" data-target="enrich-feature-dim-l" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Depth:</span>
                            <span class="feature-value" id="enrich-feature-dim-d">${data.clock_features?.dimensions_d || 'Not recorded'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.dimensions_d" data-target="enrich-feature-dim-d" style="margin-left:8px">Edit</button>
                        </div>
                        <div class="feature-item">
                            <span class="feature-label">Height:</span>
                            <span class="feature-value" id="enrich-feature-dim-h">${data.clock_features?.dimensions_h || 'Not recorded'}</span>
                            <button class="edit-btn" data-fieldpath="clock_features.dimensions_h" data-target="enrich-feature-dim-h" style="margin-left:8px">Edit</button>
                        </div>
                    </div>
                </div>

                <!-- Building Information -->
                <div class="enrichment-section">
                    <h4>🏛️ Building Information</h4>
                    <div class="data-grid">
                        <div class="data-item">
                            <dt>Building Name</dt>
                            <dd>
                                <span id="enrich-building-name">${data.building_info?.name || 'Unknown Building'}</span>
                                <button id="edit-building-name" class="edit-btn" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                        <div class="data-item">
                            <dt>Construction Period</dt>
                            <dd>
                                <span id="enrich-building-built">${data.building_info?.built || 'Not specified'}</span>
                                <button class="edit-btn" data-fieldpath="building_info.built" data-target="enrich-building-built" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                        <div class="data-item">
                            <dt>Current Custodian</dt>
                            <dd>
                                <span id="enrich-building-custodian">${data.building_info?.custodian || 'Not specified'}</span>
                                <button class="edit-btn" data-fieldpath="building_info.custodian" data-target="enrich-building-custodian" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                    </div>
                </div>

                <!-- Location -->
                <div class="enrichment-section">
                    <h4>📍 Location</h4>
                    <div class="data-grid">
                        <div class="data-item">
                            <dt>Building Address</dt>
                            <dd>
                                ${data.location?.address || 'Not available'}
                                ${data.address_metadata?.confidence ? `
                                    <span class="confidence-badge confidence-${data.address_metadata.confidence}" title="Source: ${data.address_metadata.source || 'unknown'}">
                                        ${data.address_metadata.confidence === 'high' ? '✓ High' : data.address_metadata.confidence === 'medium' ? '~ Medium' : '? Low'} confidence
                                    </span>
                                ` : ''}
                            </dd>
                        </div>
                        ${data.location?.viewpoint_address ? `
                        <div class="data-item">
                            <dt>Viewpoint Address</dt>
                            <dd>
                                <span class="viewpoint-address" title="This is where the photographer stood, not the building location">
                                    ${data.location.viewpoint_address}
                                </span>
                                <span class="meta" style="font-size: 0.75rem; color: #666;">(approximate camera location)</span>
                            </dd>
                        </div>
                        ` : ''}
                        <div class="data-item">
                            <dt>Administrative Areas</dt>
                            <dd>${(data.location?.admin_areas || []).join(', ') || 'Not available'}</dd>
                        </div>
                    </div>
                </div>

                ${data.civic_data ? `
                <!-- Civic Context (MapIt) -->
                <div class="enrichment-section">
                    <h4>🏛️ Civic context</h4>
                    ${isAdmin ? `
                    <div style="margin:6px 0 10px 0">
                        <button id="admin-refresh-all" class="edit-btn" style="margin-left:0">Re-run full enrichment</button>
                        <span id="admin-refresh-all-status" class="meta" style="margin-left:8px"></span>
                    </div>
                    ` : ''}
                    <div class="data-grid">
                        ${data.civic_data.constituency ? `
                        <div class="data-item">
                            <dt>Constituency</dt>
                            <dd>
                                <a href="${data.civic_data.constituency.url}" target="_blank" rel="noopener">
                                    ${data.civic_data.constituency.name}
                                </a>
                                ${data.civic_data.constituency.type ? `<span class="badge" style="margin-left:6px">${data.civic_data.constituency.type}</span>` : ''}
                                ${data.civic_data.constituency.codes?.gss ? `<span class="meta"> (GSS: ${data.civic_data.constituency.codes.gss})</span>` : ''}
                            </dd>
                        </div>` : ''}

                        ${data.civic_data.council ? `
                        <div class="data-item">
                            <dt>Council</dt>
                            <dd>
                                <a href="${data.civic_data.council.url}" target="_blank" rel="noopener">
                                    ${data.civic_data.council.name}
                                </a>
                                ${data.civic_data.council.type ? `<span class="badge" style="margin-left:6px">${data.civic_data.council.type}</span>` : ''}
                                ${data.civic_data.council.codes?.gss ? `<span class="meta"> (GSS: ${data.civic_data.council.codes.gss})</span>` : ''}
                            </dd>
                        </div>` : ''}

                        ${data.civic_data.ward ? `
                        <div class="data-item">
                            <dt>Ward</dt>
                            <dd>
                                <a href="${data.civic_data.ward.url}" target="_blank" rel="noopener">
                                    ${data.civic_data.ward.name}
                                </a>
                                ${data.civic_data.ward.type ? `<span class="badge" style="margin-left:6px">${data.civic_data.ward.type}</span>` : ''}
                            </dd>
                        </div>` : ''}

                        ${data.civic_data.parish ? `
                        <div class="data-item">
                            <dt>Parish/Community</dt>
                            <dd>
                                <a href="${data.civic_data.parish.url}" target="_blank" rel="noopener">
                                    ${data.civic_data.parish.name}
                                </a>
                                ${data.civic_data.parish.type ? `<span class="badge" style="margin-left:6px">${data.civic_data.parish.type}</span>` : ''}
                            </dd>
                        </div>` : ''}

                        ${data.civic_data.country ? `
                        <div class="data-item">
                            <dt>Country</dt>
                            <dd>${({E:'England', W:'Wales', S:'Scotland', N:'Northern Ireland'})[data.civic_data.country] || data.civic_data.country}</dd>
                        </div>` : ''}
                    </div>

                    ${(data.civic_data.geometry?.council?.available || data.civic_data.geometry?.constituency?.available) ? `
                    <div class="boundary-container" style="margin-top:10px">
                        <div id="boundary-map" style="height:240px; margin-top:8px; border-radius:8px; overflow:hidden;"></div>
                    </div>` : ''}
                </div>
                ` : ''}

                <!-- Heritage -->
                <div class="enrichment-section">
                    <h4>📜 Heritage</h4>
                    <div class="data-grid">
                        <div class="data-item">
                            <dt>Status</dt>
                            <dd>${data.heritage?.status || 'Not specified'}</dd>
                        </div>
                        <div class="data-item">
                            <dt>Grade</dt>
                            <dd>${data.heritage?.grade || 'Not applicable'}</dd>
                        </div>
                    </div>
                </div>

                <!-- Church Heritage Section (if this is a church) -->
                ${data.church_heritage?.is_church ? `
                <div class="enrichment-section church-heritage-section">
                    <h4>⛪ Church Heritage</h4>
                    <div class="church-heritage-badges">
                        ${data.church_heritage.is_cct ? '<span class="church-badge cct">Churches Conservation Trust</span>' : ''}
                        ${data.church_heritage.denomination ? `<span class="church-badge denomination">${this.formatDenomination(data.church_heritage.denomination)}</span>` : ''}
                        ${data.church_heritage.organization ? `<span class="church-badge organization">${data.church_heritage.organization}</span>` : ''}
                    </div>
                    <div class="data-grid">
                        ${data.church_heritage.denomination ? `
                        <div class="data-item">
                            <dt>Denomination</dt>
                            <dd>${this.formatDenomination(data.church_heritage.denomination)}</dd>
                        </div>` : ''}
                        ${data.church_heritage.religion ? `
                        <div class="data-item">
                            <dt>Religion</dt>
                            <dd>${this.formatReligion(data.church_heritage.religion)}</dd>
                        </div>` : ''}
                        ${data.church_heritage.organization ? `
                        <div class="data-item">
                            <dt>Organization</dt>
                            <dd>${data.church_heritage.organization}</dd>
                        </div>` : ''}
                        ${data.church_heritage.operator ? `
                        <div class="data-item">
                            <dt>Operator</dt>
                            <dd>${data.church_heritage.operator}</dd>
                        </div>` : ''}
                        ${data.church_heritage.building_type ? `
                        <div class="data-item">
                            <dt>Building Type</dt>
                            <dd>${this.formatBuildingType(data.church_heritage.building_type)}</dd>
                        </div>` : ''}
                        ${data.church_heritage.is_cct ? `
                        <div class="data-item cct-info">
                            <dt>CCT Status</dt>
                            <dd>
                                <a href="https://www.visitchurches.org.uk" target="_blank" rel="noopener">
                                    Churches Conservation Trust
                                </a>
                                <span class="cct-note">Historic redundant church</span>
                            </dd>
                        </div>` : ''}
                        <div class="data-item">
                            <dt>Data Source</dt>
                            <dd>${data.church_heritage.source === 'osm_operator_tag' ? 'OpenStreetMap (verified)' :
                                  data.church_heritage.source === 'osm_amenity_tag' ? 'OpenStreetMap' :
                                  data.church_heritage.source === 'osm_retroactive' ? 'OpenStreetMap (retroactive)' :
                                  data.church_heritage.source || 'Unknown'}</dd>
                        </div>
                    </div>
                </div>
                ` : ''}

                <!-- Horology Data Section -->
                ${data.horology_data ? `
                    <div class="enrichment-section horology-section">
                        <h4>🕰️ Horology & Heritage Data</h4>
                        
                        <!-- Historic England -->
                        ${data.horology_data.historic_england ? `
                            <div class="horology-subsection">
                                <h5>🏛️ Historic England</h5>
                                <div class="data-grid">
                                    <div class="data-item">
                                        <dt>Building Name</dt>
                                        <dd>${data.horology_data.historic_england.building_name || 'Not found'}</dd>
                                    </div>
                                    <div class="data-item">
                                        <dt>Grade</dt>
                                        <dd>${data.horology_data.historic_england.grade || 'Not listed'}</dd>
                                    </div>
                                    <div class="data-item">
                                        <dt>List Entry</dt>
                                        <dd>${data.horology_data.historic_england.list_entry || 'N/A'}</dd>
                                    </div>
                                    <div class="data-item">
                                        <dt>NGR</dt>
                                        <dd>${data.horology_data.historic_england.ngr || 'N/A'}</dd>
                                    </div>
                                    <div class="data-item">
                                        <dt>Search Strategy</dt>
                                        <dd>${data.horology_data.historic_england.search_strategy || 'N/A'}</dd>
                                    </div>
                                    <div class="data-item">
                                        <dt>Buildings Found</dt>
                                        <dd>${data.horology_data.historic_england.total_buildings_found || 0}</dd>
                                    </div>
                                </div>
                            </div>
                        ` : ''}
                    </div>
                ` : ''}

                <!-- AHS Turret Clock Data Section -->
                <div class="enrichment-section ahs-section">
                    <h4>🕰️ AHS Turret Clock Data</h4>
                    <p class="section-note">Data from the <a href="https://www.ahsoc.org/groups/turret-clock-group/" target="_blank" rel="noopener">Antiquarian Horological Society Turret Clock Group</a></p>
                    <div class="data-grid">
                        <div class="data-item">
                            <dt>AHS Clock ID</dt>
                            <dd>
                                <span id="enrich-ahs-clock-id">${data.ahs_data?.ahs_clock_id || 'Not recorded'}</span>
                                <button class="edit-btn" data-fieldpath="ahs_data.ahs_clock_id" data-target="enrich-ahs-clock-id" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                        <div class="data-item">
                            <dt>Construction Date</dt>
                            <dd>
                                <span id="enrich-ahs-construction-date">${data.ahs_data?.construction_date || 'Not recorded'}</span>
                                <button class="edit-btn" data-fieldpath="ahs_data.construction_date" data-target="enrich-ahs-construction-date" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                        <div class="data-item full-width">
                            <dt>AHS Notes</dt>
                            <dd>
                                <span id="enrich-ahs-notes">${data.ahs_data?.ahs_notes || 'Not recorded'}</span>
                                <button class="edit-btn" data-fieldpath="ahs_data.ahs_notes" data-target="enrich-ahs-notes" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                    </div>
                </div>

                <!-- Data Quality -->
                ${data.data_quality?.needs_review ? `
                    <div class="enrichment-section">
                        <h4>📊 Data Quality</h4>
                        <div class="quality-indicator quality-review">
                            ⚠️ Needs Review
                        </div>
                        <p class="review-notes">
                            <strong>Review Notes:</strong> <span id="enrich-dq-notes">${data.data_quality.review_notes || ''}</span>
                            <button class="edit-btn" data-fieldpath="data_quality.review_notes" data-target="enrich-dq-notes" style="margin-left:8px">Edit</button>
                        </p>
                    </div>
                ` : ''}

                <div class="enrichment-section">
                    <h4>🧪 Data Quality Controls</h4>
                    <div class="data-grid">
                        <div class="data-item">
                            <dt>Needs Review</dt>
                            <dd>
                                <span id="enrich-dq-needs">${(data.data_quality?.needs_review ? 'true' : 'false')}</span>
                                <button class="edit-btn" data-fieldpath="data_quality.needs_review" data-target="enrich-dq-needs" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                        <div class="data-item">
                            <dt>Review Notes</dt>
                            <dd>
                                <span id="enrich-dq-notes-alt">${data.data_quality?.review_notes || ''}</span>
                                <button class="edit-btn" data-fieldpath="data_quality.review_notes" data-target="enrich-dq-notes-alt" style="margin-left:8px">Edit</button>
                            </dd>
                        </div>
                    </div>
                </div>

                <!-- External Links -->
                ${actualSources.length > 0 ? `
                    <div class="enrichment-section">
                        <h4>🔗 External Sources</h4>
                        <div class="external-links">
                            ${actualSources.filter(source => source.url).map(source => `
                                <a href="${source.url}" target="_blank" rel="noopener noreferrer" class="external-link">
                                    📖 ${source.name}
                                </a>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}

                <!-- Summary -->
                <div class="enrichment-section">
                    <h4>📝 Summary</h4>
                    <p class="summary-text">
                        <span id="enrich-summary-text">${summaryText}</span>
                        <button class="edit-btn" data-fieldpath="summary.text" data-target="enrich-summary-text" style="margin-left:8px">Edit</button>
                    </p>
                </div>
            </div>
        `;

        // After render: initialize boundary map if present
        try {
            const mapEl = document.getElementById('boundary-map');
            if (mapEl) {
                const geoUrl = (data.civic_data?.geometry?.council?.available && data.civic_data.geometry.council.url)
                    || (data.civic_data?.geometry?.constituency?.available && data.civic_data.geometry.constituency.url)
                    || null;
                if (geoUrl) {
                    await this.ensureLeafletLoaded();
                    let initLat = null, initLon = null;
                    const latAttr = document.querySelector('[data-latitude]')?.dataset?.latitude;
                    const lonAttr = document.querySelector('[data-longitude]')?.dataset?.longitude;
                    const latNum = latAttr !== undefined ? parseFloat(latAttr) : NaN;
                    const lonNum = lonAttr !== undefined ? parseFloat(lonAttr) : NaN;
                    if (Number.isFinite(latNum) && Number.isFinite(lonNum)) {
                        initLat = latNum; initLon = lonNum;
                    }
                    const map = L.map(mapEl, { zoomControl: false, dragging: true, scrollWheelZoom: false });
                    if (initLat !== null && initLon !== null) {
                        map.setView([initLat, initLon], 12);
                    }
                    L.tileLayer('https://watercolormaps.collection.cooperhewitt.org/tile/watercolor/{z}/{x}/{y}.jpg', {
                        maxZoom: 18,
                        attribution: '© Cooper Hewitt, © Stamen Design, © OpenStreetMap'
                    }).addTo(map);
                    const resp = await fetch(geoUrl);
                    if (resp.ok) {
                        const gj = await resp.json();
                        const layerRef = L.geoJSON(gj, { style: { color: '#2c5aa0', weight: 2, fillOpacity: 0.05 } }).addTo(map);
                        try {
                            const bounds = layerRef.getBounds();
                            if (bounds && bounds.isValid()) {
                                map.fitBounds(bounds, { padding: [10, 10], maxZoom: 13 });
                            }
                        } catch (e) {}
                    }
                }
            }
        } catch (e) {
            console.log('Boundary init skipped:', e.message);
        }

        // Admin-only full refresh wiring
        try {
            const btn = document.getElementById('admin-refresh-all');
            const statusEl = document.getElementById('admin-refresh-all-status');
            if (btn) {
                btn.addEventListener('click', async () => {
                    try {
                        const token = localStorage.getItem('authToken');
                        if (!token) {
                            statusEl.textContent = 'Login required';
                            return;
                        }
                        statusEl.textContent = 'Re-running…';
                        // Build best-effort clockData with coordinates and content
                        let lat = this.currentClockData?.coordinates?.latitude || this.currentClockData?.LATITUDE || this.currentClockData?.latitude || null;
                        let lon = this.currentClockData?.coordinates?.longitude || this.currentClockData?.LONGITUDE || this.currentClockData?.longitude || null;
                        if ((lat === null || lat === undefined) || (lon === null || lon === undefined)) {
                            const latAttr = document.querySelector('[data-latitude]')?.dataset?.latitude;
                            const lonAttr = document.querySelector('[data-longitude]')?.dataset?.longitude;
                            const latNum = latAttr !== undefined ? parseFloat(latAttr) : NaN;
                            const lonNum = lonAttr !== undefined ? parseFloat(lonAttr) : NaN;
                            if (Number.isFinite(latNum) && Number.isFinite(lonNum)) { lat = latNum; lon = lonNum; }
                        }
                        const payload = {
                            clockId: this.currentClockId,
                            clockData: {
                                title: this.currentClockData?.TITLE || this.currentClockData?.title || '',
                                description: this.currentClockData?.DESCRIPTION || this.currentClockData?.description || '',
                                coordinates: { latitude: lat, longitude: lon }
                            },
                            forceRefreshAll: true
                        };
                        const ENRICHMENT_API_URL = 'https://b1oha2wc98.execute-api.us-east-1.amazonaws.com/prod/enrichment';
                        const resp = await fetch(ENRICHMENT_API_URL, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(payload)
                        });
                        if (!resp.ok) {
                            const err = await resp.json().catch(() => ({}));
                            throw new Error(err.message || `HTTP ${resp.status}`);
                        }
                        const result = await resp.json();
                        statusEl.textContent = 'Done';
                        // Re-render with updated data
                        if (result?.data) {
                            this.renderEnrichmentData(result.data);
                        }
                    } catch (err) {
                        statusEl.textContent = `Error: ${err.message}`;
                    }
                });
            }
        } catch (e) {
            // ignore
        }
    }

    async ensureLeafletLoaded() {
        if (window.L) return;
        await this.loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
        await this.loadCss('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
    }

    loadScript(src) {
        return new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = src;
            s.onload = () => resolve();
            s.onerror = () => reject(new Error('Failed to load ' + src));
            document.head.appendChild(s);
        });
    }

    loadCss(href) {
        return new Promise((resolve, reject) => {
            const l = document.createElement('link');
            l.rel = 'stylesheet';
            l.href = href;
            l.onload = () => resolve();
            l.onerror = () => reject(new Error('Failed to load ' + href));
            document.head.appendChild(l);
        });
    }

    /**
     * Close the enrichment modal
     */
    closeModal() {
        this.modal.classList.remove('active');
        this.currentClockId = null;
        this.isProcessing = false;
    }
}

// Initialize enrichment service when DOM is loaded
let enrichmentService;
console.log('🔧 Setting up DOMContentLoaded listener for enrichment service');

document.addEventListener('DOMContentLoaded', function() {
    console.log('🔧 DOMContentLoaded fired for enrichment service');
    console.log('🔍 Document ready state at DOMContentLoaded:', document.readyState);
    
    try {
        console.log('🔧 Creating new EnrichmentService instance...');
        enrichmentService = new EnrichmentService();
        console.log('🔧 EnrichmentService instance created:', enrichmentService);
        
        // Expose globally for button clicks
        console.log('🔧 Setting window.enrichmentService...');
        window.enrichmentService = enrichmentService;
        console.log('✅ window.enrichmentService set successfully');
        console.log('🔍 window.enrichmentService type:', typeof window.enrichmentService);
        console.log('🔍 window.enrichmentService value:', window.enrichmentService);
        
    } catch (error) {
        console.error('❌ Error initializing enrichment service:', error);
        window.enrichmentService = null;
    }
});

// Also try to initialize immediately if DOM is already loaded
if (document.readyState === 'loading') {
    console.log('🔧 DOM still loading, waiting for DOMContentLoaded...');
} else {
    console.log('🔧 DOM already loaded, initializing immediately...');
    try {
        enrichmentService = new EnrichmentService();
        window.enrichmentService = enrichmentService;
        console.log('✅ Enrichment service initialized immediately');
    } catch (error) {
        console.error('❌ Error initializing enrichment service immediately:', error);
    }
}

// Toggle function for system explanation collapsible section
window.toggleSystemExplanation = function() {
    const content = document.querySelector('.system-explanation-content');
    const toggle = document.querySelector('.system-explanation-toggle');

    if (content && toggle) {
        if (content.style.display === 'none') {
            content.style.display = 'block';
            toggle.textContent = '▲';
        } else {
            content.style.display = 'none';
            toggle.textContent = '▼';
        }
    }
};
